/**
 * Startup Insights Hook
 *
 * React hook for managing Startup Insights sessions.
 * Session-only conversations (messages not persisted to server).
 */

import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  sendInsightsMessage,
  type InsightsMessage,
  type InsightsStartupContext,
  type InsightsRequest,
  type InsightsResponse,
  type InsightsActionItem,
  type InsightsFocusArea,
} from '@/lib/supabase/edge-functions';
import type { StartupProfile } from '@/lib/state/profile-store';
import type { ScoreCriteria, AIRecommendation } from '@/lib/scoring/types';
import { reportError } from '@/lib/errors';

// =============================================================================
// Types
// =============================================================================

export interface UseStartupInsightsOptions {
  /** Initial startup context */
  startupContext?: InsightsStartupContext;
  /** Callback when message sent successfully */
  onMessageSent?: (message: InsightsMessage) => void;
  /** Callback when action items extracted */
  onActionItems?: (items: InsightsActionItem[]) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

export interface UseStartupInsightsReturn {
  /** Current message history */
  messages: InsightsMessage[];
  /** Whether a message is being processed */
  isLoading: boolean;
  /** Last error */
  error: Error | null;
  /** Send a message */
  sendMessage: (content: string, focusArea?: InsightsFocusArea) => void;
  /** Clear conversation history */
  clearHistory: () => void;
  /** Update startup context */
  setStartupContext: (context: InsightsStartupContext) => void;
  /** Current startup context */
  startupContext: InsightsStartupContext | null;
  /** Suggested follow-up questions */
  suggestedQuestions: string[];
  /** Action items from conversation */
  actionItems: InsightsActionItem[];
  /** Topics discussed in conversation */
  topicsDiscussed: string[];
  /** Total tokens used in session */
  sessionTokens: number;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Generate a unique message ID
 */
function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Convert StartupProfile to InsightsStartupContext
 */
export function profileToInsightsContext(profile: StartupProfile): InsightsStartupContext {
  // Convert array fields to string format
  const competitorsString = profile.competitors?.join(', ');
  const previousVenturesString = profile.previousVentures?.join(', ');
  const marketSizeString = profile.marketSize
    ? `$${(profile.marketSize / 1000000).toFixed(1)}M`
    : undefined;

  // Convert fundraising history to summary string
  const fundraisingHistoryString = profile.fundraisingHistory?.length
    ? profile.fundraisingHistory
        .map((r) => `${r.stage || 'Round'}: $${(r.amount / 1000000).toFixed(1)}M (${r.date || 'N/A'})`)
        .join('; ')
    : undefined;

  // Convert Swiss verification to simple format
  const swissVerification = profile.swissVerification?.zefix
    ? {
        isSwissStartup: true,
        registrationStatus: profile.swissVerification.zefix.status || 'verified',
      }
    : undefined;

  // Convert Startupticker data to simple format
  const startuptickerData = profile.startuptickerData?.profile
    ? {
        featured: true,
        category: profile.startuptickerData.profile.sector,
      }
    : undefined;

  return {
    name: profile.name || 'Unnamed Startup',
    stage: profile.stage,
    industry: profile.industry,
    location: profile.location,
    foundedYear: profile.foundedYear,
    website: profile.website,
    teamSize: profile.teamSize,
    isSerialFounder: profile.isSerialFounder,
    previousVentures: previousVenturesString,
    tagline: profile.tagline,
    description: profile.description,
    problem: profile.problem,
    solution: profile.solution,
    targetCustomer: profile.targetCustomer,
    monthlyRevenue: profile.monthlyRevenue,
    userCount: profile.userCount,
    growthRate: profile.growthRate,
    marketSize: marketSizeString,
    competitors: competitorsString,
    fundingTarget: profile.fundingTarget,
    minimumTicket: profile.minimumTicket,
    isActivelyRaising: profile.isActivelyRaising,
    fundraisingHistory: fundraisingHistoryString,
    swissVerification,
    startuptickerData,
    dataRoomConnected: profile.dataRoomConnected,
    dataRoomProvider: profile.dataRoomProvider,
  };
}

/**
 * Add score data to context
 */
export function addScoresToInsightsContext(
  context: InsightsStartupContext,
  scores: {
    finalScore?: number;
    readinessLevel?: string;
    dimensions?: Array<{
      dimension: string;
      name: string;
      score: number;
      feedback?: string;
      strength?: string;
      weakness?: string;
    }>;
    topRisks?: string[];
    topStrengths?: string[];
    recommendations?: string[];
  }
): InsightsStartupContext {
  return {
    ...context,
    scores,
  };
}

/**
 * Convert ScoreCriteria to dimension format for context
 */
export function scoreCriteriaToContextDimensions(
  criteria: ScoreCriteria[]
): Array<{
  dimension: string;
  name: string;
  score: number;
  feedback?: string;
  strength?: string;
  weakness?: string;
}> {
  return criteria.map((c) => ({
    dimension: c.dimension,
    name: c.name,
    score: Math.round((c.score / c.maxScore) * 100),
    feedback: c.feedback,
    strength: c.recommendation?.includes('strength') ? c.recommendation : undefined,
    weakness: c.recommendation,
  }));
}

/**
 * Convert an InsightsActionItem to an AIRecommendation for the recommendations tab.
 */
export function insightsActionItemToRecommendation(item: InsightsActionItem): AIRecommendation {
  return {
    id: `insights_${item.id}`,
    area: item.dimension || 'AI Advisor Suggestion',
    priority: item.priority,
    currentScore: 0,
    potentialScore: 0,
    suggestion: item.action,
    actionItems: [item.action],
    source: 'insights',
  };
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for Startup Insights functionality
 */
export function useStartupInsights(options: UseStartupInsightsOptions = {}): UseStartupInsightsReturn {
  const [messages, setMessages] = useState<InsightsMessage[]>([]);
  const [startupContext, setStartupContext] = useState<InsightsStartupContext | null>(
    options.startupContext || null
  );
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [actionItems, setActionItems] = useState<InsightsActionItem[]>([]);
  const [topicsDiscussed, setTopicsDiscussed] = useState<string[]>([]);
  const [sessionTokens, setSessionTokens] = useState(0);

  // Mutation for sending messages
  const { mutate: insightsMutate, reset: insightsReset, isPending: insightsIsPending, error: insightsError } = useMutation({
    mutationFn: async ({ content, focusArea }: { content: string; focusArea?: InsightsFocusArea }) => {
      if (!startupContext) {
        throw new Error('No startup context set. Please complete your profile first.');
      }

      // Create user message
      const userMessage: InsightsMessage = {
        id: generateMessageId(),
        role: 'user',
        content,
        timestamp: Date.now(),
      };

      // Optimistically add user message
      setMessages((prev) => [...prev, userMessage]);

      // Build request
      const request: InsightsRequest = {
        message: content,
        history: messages,
        startupContext,
        focusArea,
      };

      // Send to API
      const response = await sendInsightsMessage(request);

      if (response.error || !response.data?.success || !response.data.data) {
        throw new Error(response.error || response.data?.error || 'Failed to get response');
      }

      return {
        assistantMessage: response.data.data.message,
        suggestedQuestions: response.data.data.suggestedQuestions || [],
        actionItems: response.data.data.actionItems || [],
        topicsDiscussed: response.data.data.topicsDiscussed || [],
        usage: response.data.usage,
      };
    },
    onSuccess: (result) => {
      // Add assistant message to history
      setMessages((prev) => [...prev, result.assistantMessage]);

      // Update suggested questions
      setSuggestedQuestions(result.suggestedQuestions);

      // Merge action items (deduplicate by action text)
      setActionItems((prev) => {
        const existing = new Set(prev.map((a) => a.action));
        const newItems = result.actionItems.filter((a) => !existing.has(a.action));
        return [...prev, ...newItems];
      });

      // Merge topics discussed
      setTopicsDiscussed((prev) => {
        const combined = new Set([...prev, ...result.topicsDiscussed]);
        return Array.from(combined);
      });

      // Update token count
      if (result.usage) {
        setSessionTokens((prev) => prev + result.usage!.totalTokens);
      }

      // Call success callbacks
      options.onMessageSent?.(result.assistantMessage);
      if (result.actionItems.length > 0) {
        options.onActionItems?.(result.actionItems);
      }
    },
    onError: (error) => {
      // A consent refusal is not a chat failure — InsightsContainer detects
      // `error.message === 'Consent required'` and opens the consent sheet
      // instead of showing an error. Appending "Sorry, I encountered an
      // error" here would leave that dead-end bubble in the transcript
      // ANYWAY, underneath the sheet, so it is skipped for this one case.
      if (error instanceof Error && error.message === 'Consent required') {
        reportError(error, { context: 'useStartupInsights.sendMessage', level: 'warning', silent: true });
        options.onError?.(error);
        return;
      }

      // Keep user message visible but add error notice
      const errorMessage: InsightsMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your message. Please try again.',
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMessage]);

      reportError(error, { context: 'useStartupInsights.sendMessage', level: 'warning' });
      options.onError?.(error instanceof Error ? error : new Error(String(error)));
    },
  });

  // Send message handler
  const sendMessage = useCallback(
    (content: string, focusArea?: InsightsFocusArea) => {
      if (!content.trim()) return;
      insightsMutate({ content: content.trim(), focusArea });
    },
    [insightsMutate]
  );

  // Clear history handler
  const clearHistory = useCallback(() => {
    setMessages([]);
    setSuggestedQuestions([]);
    setActionItems([]);
    setTopicsDiscussed([]);
    setSessionTokens(0);
    insightsReset();
  }, [insightsReset]);

  // Update context handler
  const updateContext = useCallback((context: InsightsStartupContext) => {
    setStartupContext(context);
  }, []);

  return {
    messages,
    isLoading: insightsIsPending,
    error: insightsError,
    sendMessage,
    clearHistory,
    setStartupContext: updateContext,
    startupContext,
    suggestedQuestions,
    actionItems,
    topicsDiscussed,
    sessionTokens,
  };
}
